import { Loader2 } from 'lucide-react'
import { formatMoney, parseMoney } from '@lib/helpers/money'
import { formatMinutesShort } from '@lib/helpers/minutes'
import { IRRIGATION_LABOR_BILLING } from '@lib/helpers/irrigation-pricing'
import type { IrrigationEstimateDto } from '@lib/dto/irrigation.dto'
import { describeUnpriceable } from '@/irrigation-survey/RepairEstimate'
import {
  AttentionLine,
  BidSource,
  BidTrayFrame,
  OtherProperties,
  TaxLines,
  TraySection,
  TrayRow,
  countAndTime,
} from './BidTray'
import type { IrrigationBidTotal } from './useIrrigationBidTotal'

export interface IrrigationBidTrayProps {
  id: string
  total: IrrigationBidTotal
  siteName: string
  variant: 'overlay' | 'column'
  /** Changes on this phone the server has not priced yet. */
  queuedCount: number
  isOnline: boolean
  className?: string | undefined
  onClose: () => void
  onChooseProposal: (proposalId: string) => void
}

/** "3:42 PM". */
function formatTime(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Why the figure may be behind the screen: unsaved changes, or no signal. */
function pendingNote(queuedCount: number, isOnline: boolean): string {
  const after = isOnline
    ? 'It updates once they’re saved.'
    : 'It updates once there’s a signal.'
  if (queuedCount === 0) return after
  const changes =
    queuedCount === 1
      ? '1 change on this phone isn’t priced yet.'
      : `${String(queuedCount)} changes on this phone aren’t priced yet.`
  return `${changes} ${after}`
}

/**
 * Why billed labor is more than recorded labor: the minimum, or the rounding
 * up to the next increment. Null when they are the same.
 */
function billingNote(estimate: IrrigationEstimateDto): string | null {
  const { laborMinutes, billedMinutes } = estimate
  if (billedMinutes <= laborMinutes) return null
  const billed = formatMinutesShort(billedMinutes) ?? ''
  const reason =
    laborMinutes < IRRIGATION_LABOR_BILLING.minimumMinutes &&
    billedMinutes === IRRIGATION_LABOR_BILLING.minimumMinutes
      ? 'the 1-hour minimum'
      : `rounded up to the next ${String(IRRIGATION_LABOR_BILLING.incrementMinutes)} minutes`
  return `Labor billed as ${billed}, ${reason}.`
}

function SiteRows({ estimate }: { estimate: IrrigationEstimateDto }) {
  const drive = parseMoney(estimate.driveCharge?.price ?? null)
  const repairsCents = (parseMoney(estimate.amount) ?? 0) - (drive ?? 0)
  const note = billingNote(estimate)
  if (estimate.pricedRepairCount === 0) {
    return (
      <p className="text-xs text-muted-text">No repairs priced here yet.</p>
    )
  }
  return (
    <>
      <TrayRow
        label="Repairs"
        detail={countAndTime(
          estimate.pricedRepairCount,
          'repair',
          estimate.billedMinutes,
        )}
        cents={repairsCents}
      />
      {estimate.driveCharge === null || drive === null ? null : (
        <TrayRow
          label="Drive charge"
          detail={estimate.driveCharge.town}
          cents={drive}
        />
      )}
      {note === null ? null : (
        <p className="pt-0.5 text-xs text-muted-text">{note}</p>
      )}
    </>
  )
}

/**
 * What the irrigation on a bid comes to, while the survey is being edited.
 *
 * Priced by the office from Housecall Pro, so it reflects what has synced.
 * When changes on the phone have not been priced yet it says so, with when
 * the figure was priced, rather than presenting an old figure as current.
 */
export function IrrigationBidTray({
  id,
  total,
  siteName,
  variant,
  queuedCount,
  isOnline,
  className,
  onClose,
  onChooseProposal,
}: IrrigationBidTrayProps) {
  const { bidContext, estimate, others } = total
  const bid = bidContext.bid
  const coversBid = bid !== null && bid.version.sites.length > 1
  const isBehind = queuedCount > 0 || !isOnline

  function renderSite() {
    if (estimate !== null) return <SiteRows estimate={estimate} />
    if (total.isLoading) {
      return (
        <p className="flex items-center gap-1.5 text-xs text-muted-text">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Pricing the repairs…
        </p>
      )
    }
    return (
      <p className="text-xs text-secondary-text">
        Repairs are priced by the office from Housecall Pro, so they need a
        signal and this visit saved.
      </p>
    )
  }

  return (
    <BidTrayFrame
      id={id}
      title={bid === null ? 'Irrigation on this property' : 'Irrigation on this bid'}
      variant={variant}
      onClose={onClose}
      className={className}
    >
      <BidSource
        context={bidContext}
        work="irrigation"
        onChooseProposal={onChooseProposal}
      />

      <div className="border-b border-default px-3 py-3">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm text-secondary-text">
            {coversBid ? 'Whole bid' : 'Total'}
          </span>
          <span className="text-xl font-semibold tabular-nums">
            {total.totalCents === null ? '—' : formatMoney(total.totalCents)}
            {total.isIncomplete && total.totalCents !== null ? '+' : null}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-muted-text">
          {total.taxCents === null ? 'No tax added' : 'Tax included'}
          {total.pricedAt === null
            ? null
            : ` · priced at ${formatTime(total.pricedAt)}`}
        </p>
        {isBehind && estimate !== null ? (
          <p className="mt-1 text-xs text-secondary-text">
            {pendingNote(queuedCount, isOnline)}
          </p>
        ) : null}
      </div>

      {estimate !== null && estimate.unpriceable.length > 0 ? (
        <TraySection title="Missing from the total">
          <AttentionLine>
            Can’t be priced yet:{' '}
            {describeUnpriceable(
              estimate.unpriceable.map((entry) => entry.reason),
            )}
            .
          </AttentionLine>
        </TraySection>
      ) : null}

      <TraySection
        title={coversBid ? `This property · ${siteName}` : siteName}
        aside={
          coversBid && estimate !== null ? (
            <span className="text-sm font-semibold tabular-nums">
              {formatMoney(total.siteCents)}
            </span>
          ) : null
        }
      >
        {renderSite()}
      </TraySection>

      {others.state === 'none' || bid === null ? null : (
        <OtherProperties
          sites={others.state === 'ready' ? others.sites : []}
          state={others.state}
          mapPath="irrigation"
          proposalId={bid.proposal.id}
        />
      )}

      <TaxLines
        subtotalCents={total.subtotalCents}
        taxRatePercent={total.taxRatePercent}
        taxCents={total.taxCents}
        totalCents={total.totalCents}
        isIncomplete={total.isIncomplete}
      />
    </BidTrayFrame>
  )
}
